// 🔧 promotion_repository.js
// ✅ Purpose: Central Firestore repository for the LGV Promotion Engine
// ✅ Features:
// - Provides a single location for all Firestore access
// - Abstracts collection names from UI pages
// - Prepares for future service-layer business logic
// - Keeps React pages free of Firestore implementation details
// - Uses promotion_posts as the master content collection
// - Fully block-commented for clarity and future devs

import {
  collection,
  doc,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  PROMOTION_COLLECTIONS,
  PROMOTION_DOCUMENTS,
} from "../config/promotion_settings_map";

/**
 * ============================================================================
 * COLLECTION REFERENCES
 * ============================================================================
 */

/**
 * Promotion Settings
 */
export const getPromotionSettingsRef = () =>
  doc(
    db,
    PROMOTION_COLLECTIONS.SETTINGS,
    PROMOTION_DOCUMENTS.SETTINGS
  );

/**
 * Promotion Ideas
 */
export const getPromotionIdeasCollection = () =>
  collection(
    db,
    PROMOTION_COLLECTIONS.IDEAS
  );

/**
 * Promotion Posts
 *
 * Master collection for drafts, scheduled posts,
 * published posts, failed posts, and archived posts.
 */
export const getPromotionPostsCollection = () =>
  collection(
    db,
    PROMOTION_COLLECTIONS.POSTS
  );

/**
 * Promotion History
 */
export const getPromotionHistoryCollection = () =>
  collection(
    db,
    PROMOTION_COLLECTIONS.HISTORY
  );

/**
 * Promotion Campaigns
 */
export const getPromotionCampaignCollection = () =>
  collection(
    db,
    PROMOTION_COLLECTIONS.CAMPAIGNS
  );

/**
 * Prospect Follow Targets
 */
export const getPromotionFollowTargetsCollection = () =>
  collection(
    db,
    PROMOTION_COLLECTIONS.FOLLOW_TARGETS
  );

/**
 * Promotion Metrics
 */
export const getPromotionMetricsCollection = () =>
  collection(
    db,
    PROMOTION_COLLECTIONS.METRICS
  );

/**
 * ============================================================================
 * DOCUMENT REFERENCES
 * ============================================================================
 */

/**
 * Returns a Promotion Idea document reference.
 */
export const getPromotionIdeaRef = (ideaId) =>
  doc(
    db,
    PROMOTION_COLLECTIONS.IDEAS,
    ideaId
  );

/**
 * Returns a Promotion Post document reference.
 */
export const getPromotionPostRef = (postId) =>
  doc(
    db,
    PROMOTION_COLLECTIONS.POSTS,
    postId
  );

/**
 * Returns a History document reference.
 */
export const getPromotionHistoryRef = (historyId) =>
  doc(
    db,
    PROMOTION_COLLECTIONS.HISTORY,
    historyId
  );

/**
 * Returns a Campaign document reference.
 */
export const getPromotionCampaignRef = (campaignId) =>
  doc(
    db,
    PROMOTION_COLLECTIONS.CAMPAIGNS,
    campaignId
  );

/**
 * Returns a Follow Target document reference.
 */
export const getPromotionFollowTargetRef = (targetId) =>
  doc(
    db,
    PROMOTION_COLLECTIONS.FOLLOW_TARGETS,
    targetId
  );